import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { useNotification } from './NotificationContext'
import githubService from '../services/githubService'
import { flushLoginAttribution } from '../services/loginAttribution'
import { flushPendingOrgInvite } from '../services/orgInvite'
import { invalidateOrganizationSettings } from '../hooks/useOrganizationSettings'
import { apiClient, setGlobalSessionInvalidate } from '../utils/apiClient'
import { setActiveOrganizationId, clearActiveOrganizationId, reconcileActiveOrganization, syncActiveOrganizationWithJWT, type UserOrganization } from '../utils/activeOrganization'
import { dataCenterFromRegionParam, getApiUrl, resolveSelectableDC, setSelectedDC } from '../config'
import { isPublicAuthPath } from '../utils/publicRoutes'
import { getSessionToken, setSessionToken } from '../auth/session'
import { initiateOAuth, type IdentityProvider, type LinkedIdentity } from '../auth/oauth'

interface User {
  id: number
  /** Numeric GitHub account id (from JWT /api/user). */
  github_id: number
  login: string
  name: string
  avatar_url: string
  email: string
  organization_id?: number
  is_admin?: boolean
  has_github_app_access?: boolean
  github_app_installations?: string[]
  github_app_install_url?: string
  terms_accepted?: boolean
  current_terms_version?: string
  terms_accepted_version?: string
  providers?: string[]
  identities?: LinkedIdentity[]
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (provider?: IdentityProvider) => Promise<void>
  logout: () => void
  checkAuth: () => Promise<User | null>
  acceptTerms: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { showNotification } = useNotification()

  const checkAuth = useCallback(async (): Promise<User | null> => {
    try {
      const token = getSessionToken()
      if (token) {
        // Fetch user info from backend
        const userData = await apiClient.get<{ 
          user_id: string; 
          github_id: number;
          username: string;
          display_name?: string;
          email?: string;
          avatar_url?: string;
          organization_id?: number;
          is_admin?: boolean;
          has_github_app_access?: boolean;
          github_app_installations?: string[];
          github_app_install_url?: string;
          terms_accepted?: boolean;
          current_terms_version?: string;
          terms_accepted_version?: string;
          providers?: string[];
          identities?: LinkedIdentity[];
        }>('/api/user', {
          'Authorization': `Bearer ${token}`,
        }, { skipOrganizationHeader: true })

        let user: User = {
          id: Number(userData.user_id),
          github_id: userData.github_id,
          login: userData.username,
          name: userData.display_name || userData.username,
          avatar_url: userData.avatar_url || '',
          email: userData.email || '',
          organization_id: userData.organization_id,
          is_admin: userData.is_admin,
          has_github_app_access: userData.has_github_app_access,
          github_app_installations: userData.github_app_installations,
          github_app_install_url: userData.github_app_install_url,
          terms_accepted: userData.terms_accepted,
          current_terms_version: userData.current_terms_version,
          terms_accepted_version: userData.terms_accepted_version,
          providers: userData.providers,
          identities: userData.identities,
        }
        setUser(user)
        try {
          await fetch(`${getApiUrl()}/api/auth/waiting-room-session`, {
            method: 'POST',
            credentials: 'include',
            headers: { Authorization: `Bearer ${token}` },
          })
        } catch {
          /* cookie refresh is best-effort for waiting-room shared session */
        }
        try {
          const orgData = await apiClient.get<{
            organizations: UserOrganization[]
            active_organization_id: number
          }>('/api/user/organizations', undefined, { skipOrganizationHeader: true })
          const syncedOrgId = await syncActiveOrganizationWithJWT(
            userData.organization_id,
            orgData.organizations ?? [],
            orgData.active_organization_id || userData.organization_id,
          )
          if (syncedOrgId && syncedOrgId !== user.organization_id) {
            user.organization_id = syncedOrgId
            setUser({ ...user, organization_id: syncedOrgId })
          }
        } catch {
          reconcileActiveOrganization([], userData.organization_id)
        }
        await flushLoginAttribution()

        const inviteResult = await flushPendingOrgInvite()
        if (inviteResult) {
          invalidateOrganizationSettings()
          const refreshed = await apiClient.get<{ 
            user_id: string; 
            github_id: number;
            username: string;
            organization_id?: number;
            is_admin?: boolean;
            has_github_app_access?: boolean;
            github_app_installations?: string[];
            github_app_install_url?: string;
            terms_accepted?: boolean;
            current_terms_version?: string;
            terms_accepted_version?: string;
          }>('/api/user', {
            'Authorization': `Bearer ${getSessionToken()}`,
          }, { skipOrganizationHeader: true })
          user = {
            ...user,
            id: Number(refreshed.user_id),
            github_id: refreshed.github_id,
            login: refreshed.username,
            name: refreshed.username,
            organization_id: refreshed.organization_id,
          }
          setUser(user)
          if (refreshed.organization_id) {
            setActiveOrganizationId(refreshed.organization_id)
          }
        }
        return user
      } else {
        setUser(null)
        return null
      }
    } catch (error) {
      console.error('Auth check error:', error)
      console.warn('Invalid token detected, clearing authentication')
      githubService.logout()
      clearActiveOrganizationId()
      setUser(null)
      const onPublicAuth =
        typeof window !== 'undefined' && isPublicAuthPath(window.location.pathname)
      if (sessionStorage.getItem('veilstream_suppress_auth_check_toast_once')) {
        sessionStorage.removeItem('veilstream_suppress_auth_check_toast_once')
      } else if (!onPublicAuth) {
        showNotification('Your session has expired. Please log in again.', 'warning')
      }
      return null
    } finally {
      setIsLoading(false)
    }
  }, [showNotification])

  const login = async (provider: IdentityProvider = 'github') => {
    try {
      await initiateOAuth(provider)
    } catch (error) {
      console.error('Login initiation error:', error)
      const label = provider === 'google' ? 'Google' : 'GitHub'
      showNotification(`Failed to start ${label} authentication. Please try again.`, 'error')
      throw error
    }
  }

  const invalidateSessionSilently = useCallback(() => {
    githubService.logout()
    clearActiveOrganizationId()
    setUser(null)
  }, [])

  const logout = () => {
    try {
      invalidateSessionSilently()
      showNotification('Successfully logged out.', 'info')
    } catch (error) {
      console.error('Logout error:', error)
      setUser(null)
    }
  }

  const acceptTerms = async () => {
    try {
      const token = getSessionToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      await apiClient.post('/api/user/accept-terms', {}, {
        'Authorization': `Bearer ${token}`,
      })

      // Refresh user data to get updated terms status
      await checkAuth()
      showNotification('Terms and conditions accepted successfully.', 'success')
    } catch (error) {
      console.error('Accept terms error:', error)
      showNotification('Failed to accept terms and conditions. Please try again.', 'error')
      throw error
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)

    const region = params.get('region')
    if (region?.trim()) {
      const dc = dataCenterFromRegionParam(region)
      if (dc) setSelectedDC(resolveSelectableDC(dc))
      params.delete('region')
    }

    const impersonateToken = params.get('impersonate')
    if (impersonateToken) {
      // Drop prior-DC org context so the new token isn't sent with a stale org header.
      clearActiveOrganizationId()
      setSessionToken(impersonateToken)
      // Avoid "session expired" toast if checkAuth races before the hard reload below.
      sessionStorage.setItem('veilstream_suppress_auth_check_toast_once', '1')
      params.delete('impersonate')
    }

    // GitHub OAuth callback lands on /login or /join with ?token= (not only /login).
    const oauthToken = params.get('token')
    if (oauthToken?.trim()) {
      setSessionToken(oauthToken.trim())
      params.delete('token')
    }

    if (impersonateToken || oauthToken || region) {
      const newSearch = params.toString()
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash
      // Impersonate often switches data centers. history.replaceState leaves React Router's
      // location.search stale, so a failed/racy checkAuth can bounce to /login?impersonate=…
      // where the token is never re-applied. Hard-reload with a clean URL so getApiUrl() and
      // routing both see the target DC with the new token already in localStorage.
      if (impersonateToken) {
        window.location.replace(newUrl)
        return
      }
      window.history.replaceState(null, '', newUrl)
    }

    checkAuth()
  }, [checkAuth])

  useEffect(() => {
    setGlobalSessionInvalidate(invalidateSessionSilently)
  }, [invalidateSessionSilently])

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    checkAuth,
    acceptTerms,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
